'use client'

import { useTranslation } from 'react-i18next'
import * as GridLayout from '@/components/GridLayout/GridLayout'
import { logger } from '@/services/logger/logger.client'
import type { GridColumnsField, SbBaseBlockProps, StoryblokAsset } from '@/services/storyblok/storyblok'
import { getImgSrc } from '@/services/storyblok/Storyblok.helpers'
import { YouTubeEmbed } from '@next/third-parties/google'
import { storyblokEditable } from '@storyblok/react'
import { ConditionalWrapper } from 'ui/src/components/ConditionalWrapper'
import { youtubeEmbedRoot } from './YoutubeVideoBlock.css'

/** Standard YouTube video id length and charset (alphanumeric, `_`, `-`). */
const YOUTUBE_VIDEO_ID_PATTERN = /^[\w-]{11}$/

/**
 * Custom poster for `<lite-youtube>`: set `background-image` so the embed script does not overwrite it.
 * @see https://github.com/paulirish/lite-youtube-embed#custom-poster-image
 */
function buildYoutubePosterStyle(posterUrl: string): string {
  const safeForCssUrl = posterUrl.replace(/\\/g, '\\\\').replace(/'/g, "\\'")
  const safeForHtmlAttr = safeForCssUrl.replace(/&/g, '&amp;')
  return `background-image: url('${safeForHtmlAttr}')`
}

// Optimize poster URL for Storyblok assets with Storyblok Image Service
// https://www.storyblok.com/docs/api/image-service
function getOptimizedStoryblokPosterUrl(posterFilename: string): string {
  const src = getImgSrc(posterFilename)
  return `${src}/m/1600x0/filters:format(webp):quality(75)`
}

type YoutubeVideoBlockProps = SbBaseBlockProps<{
  videoId: string
  videoTitle?: string
  poster?: StoryblokAsset
  layout?: GridColumnsField
  hideControls?: boolean
}>

export function YoutubeVideoBlock({ blok, nested = false }: YoutubeVideoBlockProps) {
  const { t } = useTranslation()
  const videoId = blok.videoId?.trim() ?? ''
  if (!YOUTUBE_VIDEO_ID_PATTERN.test(videoId)) {
    logger.warn('YoutubeVideoBlock: invalid videoId, skipping render', {
      videoId: blok.videoId,
      blokUid: blok._uid,
    })
    return null
  }

  // https://developers.google.com/youtube/player_parameters?hl=en#parameters
  let params = 'rel=0'
  if (blok.hideControls) {
    params += '&controls=0'
  }
  const playlabel = blok.videoTitle ?? t('PLAY_VIDEO_LABEL')

  const posterFilename = blok.poster?.filename
  const posterStyle = posterFilename
    ? buildYoutubePosterStyle(getOptimizedStoryblokPosterUrl(posterFilename))
    : // Fallback to maxresdefault.webp if the poster is not set in Storyblok
      // https://github.com/paulirish/lite-youtube-embed/blob/master/youtube-thumbnail-urls.md
      `background-image: url('https://i.ytimg.com/vi_webp/${videoId}/maxresdefault.webp')`

  const youTubeEmbed = (
    <YouTubeEmbed
      videoid={videoId}
      params={params}
      playlabel={playlabel}
      {...(posterStyle ? { style: posterStyle } : {})}
    />
  )

  const videoContent = (
    <div {...storyblokEditable(blok)} className={youtubeEmbedRoot}>
      {youTubeEmbed}
    </div>
  )

  return (
    <ConditionalWrapper
      condition={!nested}
      wrapWith={(children) => (
        <GridLayout.Root>
          <GridLayout.Content width={blok.layout?.widths ?? { _: '1' }} align={blok.layout?.alignment ?? 'center'}>
            {children}
          </GridLayout.Content>
        </GridLayout.Root>
      )}
    >
      {videoContent}
    </ConditionalWrapper>
  )
}
